import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { PageHeader, Th } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Search, Settings2, ListChecks } from "lucide-react";

const ACTION_STYLE = { create: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", update: "bg-sky-500/10 text-sky-700 border-sky-500/30", delete: "bg-rose-500/10 text-rose-700 border-rose-500/30" };
const INIT = { scope: "config", entity: "all", user_id: "all", q: "", date_from: "", date_to: "" };

const describe = (d) => {
  if (!d || typeof d !== "object") return "";
  return Object.entries(d).filter(([k]) => !["auto"].includes(k)).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ").slice(0, 200);
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ entities: [] });
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(INIT);
  const [loading, setLoading] = useState(false);

  const load = async (filters = f) => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
    try { const r = await api.get("/audit", { params }); setRows(r.data); } finally { setLoading(false); }
  };
  useEffect(() => {
    api.get("/audit/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    load(INIT);
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [f.scope, f.entity, f.user_id, f.date_from, f.date_to]); // eslint-disable-line

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Histórico de alterações" subtitle="Quem alterou configurações, opções e registros — visível apenas para administradores.">
        <div className="flex rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
          <button onClick={() => setF({ ...f, scope: "config", entity: "all" })} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 ${f.scope === "config" ? "bg-emerald-600 text-white" : "text-slate-600"}`} data-testid="audit-scope-config"><Settings2 className="w-3.5 h-3.5" /> Configurações</button>
          <button onClick={() => setF({ ...f, scope: "all", entity: "all" })} className={`px-3 py-2 text-xs font-bold flex items-center gap-1 ${f.scope === "all" ? "bg-emerald-600 text-white" : "text-slate-600"}`} data-testid="audit-scope-all"><ListChecks className="w-3.5 h-3.5" /> Tudo</button>
        </div>
      </PageHeader>

      <Card className="p-3 border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <div className="relative col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Buscar por usuário, item ou detalhe… (Enter)" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="audit-search" />
          </div>
          <Select value={f.entity} onValueChange={(v) => setF({ ...f, entity: v })}>
            <SelectTrigger className="h-9 text-xs" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="all">Todos os itens</SelectItem>{meta.entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
            <SelectTrigger className="h-9 text-xs" data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="all">Todos os usuários</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" className="h-9 text-xs" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-date-from" />
          <Input type="date" className="h-9 text-xs" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-date-to" />
        </div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="audit-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              <tr><Th>Quando</Th><Th>Quem</Th><Th>Ação</Th><Th>Item</Th><Th>Detalhes</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-sm text-slate-500">{loading ? "Carregando…" : "Nenhum registro encontrado."}</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`audit-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">{r.user_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`text-[11px] ${ACTION_STYLE[r.action] || "text-slate-600"}`}>{r.action_label}</Badge></td>
                  <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap flex items-center gap-1.5"><History className="w-3.5 h-3.5 text-slate-400" />{r.entity_label}{r.detail?.name ? ` — ${r.detail.name}` : r.detail?.number ? ` #${r.detail.number}` : ""}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[420px] truncate" title={describe(r.detail)}>{describe(r.detail) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length >= 300 && <p className="px-4 py-2 text-xs text-slate-400">Mostrando os 300 registros mais recentes. Use os filtros para refinar.</p>}
      </Card>
      {!loading && rows.length > 0 && <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => load()} data-testid="audit-refresh">Atualizar</Button></div>}
    </div>
  );
}
